/**
 * iter87.199.190 — InviteTextCopyCard.
 *
 * A slim card mounted between LeagueMessagesCard and
 * SmackStreamAdminToggleCard on the Rivalry → Messages tab. This card
 * lives on RIVALRY (league) settings, not a specific tournament's
 * settings — so the copy is deliberately generic at the rivalry level.
 *
 * iter87.199.41 originally sourced this from a tournament-family-aware
 * backend endpoint (`GET /tournaments/{tid}/invite-copy`). Operator
 * feedback: the copy it produced was wrong for the audience (assumed
 * "teams get auto-assigned" draft-style language) AND it silently used
 * whichever tournament happened to be currently viewed (effectively
 * the newest one), not the rivalry's actual default. Since this card
 * doesn't belong to any one tournament, the fix is to drop the
 * tournament dependency entirely — one static script. No link (this
 * text is for brand-new crewmates the admin still has to manually
 * finish setting up — see `RivalryLinkCopyCard` for the "already
 * registered" case).
 *
 * iter87.199.191 — Operator asked for a more casual, first-person
 * opener ("I spun up a...") instead of the more formal/transactional
 * "You're invited to join {rivalry} rivalry as a crewmate" — no
 * rivalry-name interpolation anymore.
 */
import { useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Copy, Check } from "lucide-react";

export default function InviteTextCopyCard() {
    const [copied, setCopied] = useState(false);

    const textBody = "I spun up a super simple sports pick'em league and want you to join the fun! Really easy, all you do is complete any preseason picks and the in-season moves located in the Draft section. And that's it. Let me know if you're in and I'll get you set up.";

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(textBody);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Copied — paste into your text app");
        } catch {
            toast.error("Clipboard access blocked by your browser");
        }
    };

    return (
        <div className="brutal-card-light p-4 space-y-3" data-testid="invite-text-copy-card">
            <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-cyber flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Copy for text messages
                </h3>
                <p className="text-xs text-zinc-400 mt-1">
                    For new crewmates you'll set up yourself. Sized for iMessage / SMS / WhatsApp / Slack.
                </p>
            </div>
            <pre
                className="whitespace-pre-wrap bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white font-mono leading-relaxed"
                data-testid="invite-text-copy-body"
            >
                {textBody}
            </pre>
            <button
                onClick={doCopy}
                className="brutal-btn-primary flex items-center gap-1.5 text-xs px-3 py-2 w-full justify-center"
                data-testid="invite-text-copy-btn"
            >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied — paste into your text app" : "Copy to clipboard"}
            </button>
        </div>
    );
}
